import { faFacebook } from "@fortawesome/free-brands-svg-icons/faFacebook";
// import { faGoogleDrive } from "@fortawesome/free-brands-svg-icons/faGoogleDrive"; // paused - see tile below
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons/faWhatsapp";
import { Tooltip } from "../../../utils/local-antd";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { Clickable } from "../../../components/Clickable";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { Modal } from "../../../components/modal/Modal";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { useIntegrationContext } from "../context/integration-context";
import { useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { integrationSliceSelectors } from "../../../redux/integration/integration.selector";
import { AtiraSpacePlanType } from "../../../model/plan/types/AtiraSpacePlanType.enum";
import { useDrawersContext } from "../../tasks/drawers-context";

const IntegrationCard = styled(Flex)<{ color?: string; disabled?: boolean }>`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 10rem;
  aspect-ratio: 1 / 1;
  border: 1.5px solid ${({ theme }) => theme.main};
  border-radius: ${Rounded.lg};
  background: ${({ theme }) => theme.lightSub};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  transition: all 0.3s ease-in-out;

  svg,
  p {
    transition: all 0.3s ease-in-out;
  }

  ${({ color, disabled, theme }) =>
    !disabled &&
    css`
      &:hover {
        border-color: ${color || theme.main};
        background-color: ${color || theme.main};
        p {
          color: ${theme.white};
        }
        svg {
          color: ${theme.white};
        }
      }
    `}
`;

const IconLabel = styled(Text)`
  margin-top: ${Spacing.s};
  font-size: 1rem;
  font-weight: 500;
  text-align: center;
`;

type IntegrationAddModalProps = {
  open: boolean;
  onClose: () => void;
};

export const IntegrationAddModal: React.FC<IntegrationAddModalProps> = ({
  open,
  onClose,
}) => {
  const {
    setAddModalWhatsAppMetaVisible,
    setAddModalVisible,
    setAddModalFacebookMetaVisible,
    setFacebookMetaEditFormId,
    // setAddModalGoogleDriveVisible, // paused - see tile below
  } = useIntegrationContext();
  const { setSubToUnlock } = useDrawersContext();
  const { t } = useTranslation();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const whatsappMetaIntegration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );
  // const googleDriveIntegration = useAppSelector(
  //   integrationSliceSelectors.selectGoogleDriveIntegration,
  // );

  const isPaidPlan = user?.plan?.type !== AtiraSpacePlanType.PLAN_0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("integration.add-modal.title")}
      width={"40rem"}
    >
      <Flex flexDirection="column" gap={"s"}>
        <Text>{t("integration.add-modal.description")}</Text>
        <Text>{t("integration.add-modal.description-2")}</Text>
      </Flex>

      <Flex
        alignItems="center"
        justifyContent="center"
        gap="l"
        marginTop="l"
        flexWrap="wrap"
      >
        <Tooltip title={t("integration.add-modal.tooltip.whatsapp.meta")}>
          <Clickable
            disabled={Boolean(whatsappMetaIntegration)}
            onClick={
              isPaidPlan
                ? () => {
                    setAddModalWhatsAppMetaVisible(true);
                    setAddModalVisible(false);
                  }
                : () =>
                    setSubToUnlock({
                      visible: true,
                      title: t("subToUnlock.specific.whatsappMetaPlanLimit"),
                    })
            }
          >
            <IntegrationCard color="#25D366">
              <AtiraIcon icon={faWhatsapp} size="2x" />
              <IconLabel>
                {t("integration.add-modal.whatsapp.meta.button")}
              </IconLabel>
            </IntegrationCard>
          </Clickable>
        </Tooltip>

        <Tooltip title={t("integration.add-modal.tooltip.facebook-meta")}>
          <Clickable
            onClick={
              isPaidPlan
                ? () => {
                    setFacebookMetaEditFormId(null);
                    setAddModalFacebookMetaVisible(true);
                    setAddModalVisible(false);
                  }
                : () =>
                    setSubToUnlock({
                      visible: true,
                      title: t("subToUnlock.specific.facebookMetaPlanLimit"),
                    })
            }
          >
            <IntegrationCard color="#1877F3">
              <AtiraIcon icon={faFacebook} size="2x" />
              <IconLabel>
                {t("integration.add-modal.facebook-meta.button")}
              </IconLabel>
            </IntegrationCard>
          </Clickable>
        </Tooltip>

        {/* Paused: the full `drive` scope this needs requires Google's
        paid, annual CASA security assessment, not approved yet. Restore
        this tile (and the bindings above) once that's resolved, or once
        the feature moves to a different storage approach.
        <Tooltip title={t("integration.add-modal.tooltip.google-drive")}>
          <Clickable
            disabled={Boolean(googleDriveIntegration)}
            onClick={() => {
              setAddModalGoogleDriveVisible(true);
              setAddModalVisible(false);
            }}
          >
            <IntegrationCard color="#0F9D58">
              <AtiraIcon icon={faGoogleDrive} size="2x" />
              <IconLabel>
                {t("integration.add-modal.google-drive.button")}
              </IconLabel>
            </IntegrationCard>
          </Clickable>
        </Tooltip> */}
      </Flex>
    </Modal>
  );
};
